import { PortableText, PortableTextProps } from '@portabletext/react'
import { MlCtaBar, MlCtaBarProps } from '../ml-cta-bar'
import { Image, MlImages } from '../ml-images'

export interface MlBuildingCardProps extends MlCtaBarProps {
  title: string
  href: string
  images?: Image[]
  address: PortableTextProps['value']
  rates: {
    description: string
    rate: string
  }[]
  className?: string
  onClick?: () => void
}

export const MlBuildingCard = ({
  title,
  href,
  images,
  address,
  rates,
  className,
  onClick,
  ...ctaBarProps
}: MlBuildingCardProps) => {
  const ctaBarSizeClasses = ctaBarProps.secondaryLabel ? 'h-[6.688rem] slg:h-[7.25rem]' : 'h-14'
  const imageSizeClasses = 'mb-6 aspect-[4/3] lg:aspect-[16/9]'

  return (
    <div
      onClick={onClick}
      data-building-card
      className={` relative rounded-4 border-1 border-transparent hover:border-neutrals-400 active:border-neutrals-600 [&:has(a:focus-visible)]:border-neutrals-900 [&:has(a:focus-visible)]:rounded-0 ${className}`}
    >
      <a href={href} className="h-full p-2 lg:p-4 flex flex-col focus:outline-none relative z-[2]">
        {/* Space for the images */}
        <div className={imageSizeClasses} />

        <p className="font-heading font-medium text-[1.25rem] slg:text-24  leading-1.3 text-neutrals-900 line-clamp-1">
          {title}
        </p>
        <div className="flex flex-col gap-4 mt-4 justify-between">
          <PortableText
            value={address}
            components={{
              block: {
                normal: ({ children }) => <p className="text-neutrals-700 text-16">{children}</p>,
                strong: ({ children }) => (
                  <strong className="only:inline-block first:inline-block font-bold text-neutrals-800 leading-1.5">
                    {children}
                  </strong>
                ),
              },
            }}
          />
          {rates && rates.length > 0 ? (
            <>
              <p className="font-heading">Starting prices:</p>
              <div className="flex flex-row text-neutrals-700">
                <table className="w-full">
                  <tbody>
                    {rates.map(({ description, rate }) => (
                      <tr key={`${description}-${rate}`}>
                        <td className="w-full py-1">
                          <div className="flex">
                            <p>{description}</p>
                            <div className="mx-2 h-hairline self-center flex-1 bg-neutrals-700" />
                          </div>
                        </td>
                        <td className="min-w-[6.875rem] lg:min-w-[10.938rem]">
                          <p>{rate}</p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* White space for uneven cards */}
          <div className="flex-1" />

          {/* Space for the CTA bar */}
          <div className={ctaBarSizeClasses} />
        </div>
      </a>
      <MlImages
        unsetMaxDimensions
        aspectRatio="custom"
        className={`!absolute top-2 lg:top-4 left-2 lg:left-4 right-2 lg:right-4  ${imageSizeClasses}`}
        images={images}
        href={href}
      />
      <MlCtaBar
        {...ctaBarProps}
        parentName="MlBuildingCard"
        className={`z-10 absolute bottom-4 lg:bottom-4 left-2 lg:left-0 right-2 lg:right-0 ${ctaBarSizeClasses} !gap-3 `}
        secondaryClassName="lg:max-w-[20.974rem] px-[0.25rem]"
        primaryClassName="lg:max-w-[20.974rem] px-[0.25rem]"
      />
    </div>
  )
}
